import { useEffect, useState } from "react";
import api from "../../../lib/apiClient";

/**
 * useHeaderNotifications — polls the /notifications/summary endpoint
 * every 30 seconds for the new-follower count and inbox unread count,
 * plus the /marketplace/expired/alerts/unread-count for the bell badge.
 *
 * Listens to four "I just saw the badge" events so the corresponding
 * counters zero out instantly without waiting for the next poll:
 *   - `followers-seen`     (fired by Dashboard followers tab)
 *   - `inbox-read`         (fired by InboxPage when threads are marked read)
 *   - `expired-alerts-read`(fired by ExpiredMarketPage on dismiss)
 *   - visibilitychange     (refetch immediately on tab return)
 *
 * Skips polling when document.hidden is true so the app doesn't burn
 * battery in a background tab. Guards against post-unmount setState
 * via a cancelled flag.
 */
export default function useHeaderNotifications(user, token) {
  const [newFollowers, setNewFollowers] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [expiredAlertsUnread, setExpiredAlertsUnread] = useState(0);

  useEffect(() => {
    if (!user || !token) {
      setNewFollowers(0);
      setInboxUnread(0);
      setExpiredAlertsUnread(0);
      return undefined;
    }
    let cancelled = false;
    const fetchAll = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const [summary, alerts] = await Promise.all([
          api.get(`/notifications/summary`),
          api.get(`/marketplace/expired/alerts/unread-count`),
        ]);
        if (cancelled) return;
        setNewFollowers(summary.data.new_followers || 0);
        setInboxUnread(summary.data.inbox_unread || 0);
        setExpiredAlertsUnread(alerts.data.count || 0);
      } catch {
        /* silent — polled endpoint, transient errors are non-actionable */
      }
    };
    fetchAll();
    const intervalId = setInterval(fetchAll, 30000);
    const onSeen = () => setNewFollowers(0);
    const onInboxRead = () => setInboxUnread(0);
    const onAlertsRead = () => setExpiredAlertsUnread(0);
    const onVisible = () => {
      if (!document.hidden) fetchAll();
    };
    window.addEventListener("followers-seen", onSeen);
    window.addEventListener("inbox-read", onInboxRead);
    window.addEventListener("expired-alerts-read", onAlertsRead);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener("followers-seen", onSeen);
      window.removeEventListener("inbox-read", onInboxRead);
      window.removeEventListener("expired-alerts-read", onAlertsRead);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, token]);

  return { newFollowers, inboxUnread, expiredAlertsUnread };
}
